import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isAuthed, clearToken, adminLeadStats, adminEmailStatus } from "../../api";
import { LOGO_URL } from "../../mock";
import { LogOut, Car, Users, Inbox, CheckCircle2, MailWarning, MailCheck } from "lucide-react";
import CarManager from "./CarManager";
import LeadManager from "./LeadManager";
import MarketingHub from "./MarketingHub";
import RacingEditor from "./RacingEditor";
import HomeEditor from "./HomeEditor";
import EmailEditor from "./EmailEditor";
import ComingSoonEditor from "./ComingSoonEditor";

const AdminDashboard = () => {
  const [tab, setTab] = useState("inventory");
  const [stats, setStats] = useState(null);
  const [email, setEmail] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthed()) { navigate("/admin/login"); return; }
    adminLeadStats().then(setStats).catch((e) => {
      if (e?.response?.status === 401) { clearToken(); navigate("/admin/login"); }
    });
    adminEmailStatus().then(setEmail).catch(() => setEmail(null));
  }, [navigate, tab]);

  const logout = () => { clearToken(); navigate("/admin/login"); };

  const cards = stats ? [
    { icon: Car, label: "Cars in stock", value: stats.cars.available },
    { icon: Inbox, label: "New leads", value: stats.leads.new },
    { icon: Users, label: "Total leads", value: stats.leads.total },
    { icon: CheckCircle2, label: "Closed", value: stats.leads.closed },
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="gpm-bg-green">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GPM" className="h-10 w-10 object-contain rounded-full" />
            <span className="font-serif-display text-xl font-bold gpm-text-cream">GPM Classics — Admin</span>
          </div>
          <button onClick={logout} className="flex items-center gap-2 gpm-text-cream text-sm hover:gpm-text-gold transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {email && !email.configured && (
          <div className="mb-6 flex items-center gap-3 border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            <MailWarning size={18} />
            <span>Email alerts are <b>not yet configured</b>. Add your Gmail SMTP credentials in backend/.env (SMTP_USER + SMTP_PASSWORD) to receive new-lead alerts at <b>{email.recipient}</b>. Leads are still saved here in the CRM.</span>
          </div>
        )}
        {email && email.configured && (
          <div className="mb-6 flex items-center gap-3 border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
            <MailCheck size={18} />
            <span>Email alerts are active — new leads are emailed to <b>{email.recipient}</b>.</span>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-white border border-black/10 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center gpm-bg-green"><Icon size={22} className="gpm-text-gold" /></div>
                <div>
                  <div className="text-2xl font-bold gpm-text-green">{c.value}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{c.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 mb-6 border-b border-black/10 flex-wrap">
          {[{ k: "inventory", l: "Inventory" }, { k: "crm", l: "CRM / Leads" }, { k: "marketing", l: "Marketing" }, { k: "home", l: "Home Page" }, { k: "racing", l: "Racing Page" }, { k: "coming", l: "Coming Soon" }, { k: "email", l: "Emails" }].map((t) => (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-5 py-3 text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === t.k ? "gpm-text-green" : "text-gray-400 border-transparent hover:text-gray-600"}`} style={tab === t.k ? { borderColor: "var(--gpm-gold)" } : {}}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "inventory" && <CarManager />}
        {tab === "crm" && <LeadManager />}
        {tab === "marketing" && <MarketingHub />}
        {tab === "home" && <HomeEditor />}
        {tab === "racing" && <RacingEditor />}
        {tab === "coming" && <ComingSoonEditor />}
        {tab === "email" && <EmailEditor />}
      </div>
    </div>
  );
};

export default AdminDashboard;
